import React, {useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Card, CardContent} from "@/components/ui/card";
import {
    AlertCircle,
    BarChart4,
    CheckCircle2,
    Clock,
    Database,
    FileText,
    Lock,
    RefreshCw,
    Search,
    Settings,
    ShieldCheck,
    Sparkles,
    TrendingDown,
    TrendingUp,
    Users
} from "lucide-react";
import {cn} from "@/lib/utils";

const MODULE_CATEGORIES = {
    'document': {
        icon: FileText,
        color: 'text-blue-500',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-500',
        metrics: ['time', 'error']
    },
    'data': {
        icon: Database,
        color: 'text-purple-500',
        bgColor: 'bg-indigo-50',
        borderColor: 'border-blue-500',
        metrics: ['time', 'cost']
    },
    'workflow': {
        icon: Settings,
        color: 'text-amber-500',
        bgColor: 'bg-amber-50',
        borderColor: 'border-blue-500',
        metrics: ['time', 'cost', 'roi']
    },
    'analytics': {
        icon: Search,
        color: 'text-purple-500',
        bgColor: 'bg-purple-50',
        borderColor: 'border-blue-500',
        metrics: ['error', 'roi']
    },
    'reporting': {
        icon: BarChart4,
        color: 'text-blue-500',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-500',
        metrics: ['cost', 'time']
    },
    'compliance': {
        icon: ShieldCheck,
        color: 'text-red-500',
        bgColor: 'bg-rose-50',
        borderColor: 'border-blue-500',
        metrics: ['error', 'cost']
    },
    'collaboration': {
        icon: Users,
        color: 'text-sky-500',
        bgColor: 'bg-sky-50',
        borderColor: 'border-blue-500',
        metrics: ['time', 'roi']
    },
    'security': {
        icon: Lock,
        color: 'text-black',
        bgColor: 'bg-slate-50',
        borderColor: 'border-blue-500',
        metrics: ['error', 'cost']
    },
    'ai': {
        icon: Sparkles,
        color: 'text-blue-500',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-500',
        metrics: ['time', 'roi', 'error']
    },
    'automation': {
        icon: RefreshCw,
        color: 'text-orange-500',
        bgColor: 'bg-orange-50',
        borderColor: 'border-blue-500',
        metrics: ['cost', 'time']
    }
};

const MODULES = [
    {
        id: 'doc-extraction',
        name: 'Document Extraction',
        category: 'document',
        efficiency: 65,
        default: false,
        description: 'Automated extraction of structured data from compliance documents, forms, and regulatory filings. Reduces manual data entry by 65% and minimizes transcription errors.',
        benefits: 'Saves 4-6 hours per week on document processing tasks while improving data accuracy by 86%. Enables real-time access to critical compliance information.',
        metrics: {
            timeImpact: 'High',
            errorReduction: 'Medium',
            costSavings: 'Medium',
            roiContribution: 'Medium'
        }
    },
    {
        id: 'doc-classification',
        name: 'Document Classification',
        category: 'document',
        efficiency: 58,
        default: true,
        description: 'AI-powered automatic categorization of compliance documents by type, regulatory framework, and risk level. Organizes information for easy retrieval.',
        benefits: 'Reduces document search time by 58% and improves access to compliance evidence. Enables quick identification of related regulatory requirements.',
        metrics: {
            timeImpact: 'Medium',
            errorReduction: 'High',
            costSavings: 'Low',
            roiContribution: 'Medium'
        }
    },
    {
        id: 'data-validation',
        name: 'Data Validation',
        category: 'data',
        efficiency: 72,
        default: false,
        description: 'Automated validation of compliance data against predefined rules and regulatory requirements. Flags inconsistencies, missing fields, and potential compliance gaps.',
        benefits: 'Reduces validation errors by 72% and accelerates compliance verification. Provides immediate feedback on data quality issues.',
        metrics: {
            timeImpact: 'Medium',
            errorReduction: 'Very High',
            costSavings: 'High',
            roiContribution: 'High'
        }
    },
    {
        id: 'data-integration',
        name: 'Data Integration',
        category: 'data',
        efficiency: 63,
        default: false,
        description: 'Seamless connection of compliance data across multiple systems, databases, and third-party sources. Creates a unified view of compliance status.',
        benefits: 'Eliminates 63% of manual data consolidation tasks. Enables comprehensive compliance reporting across organizational silos.',
        metrics: {
            timeImpact: 'High',
            errorReduction: 'Medium',
            costSavings: 'High',
            roiContribution: 'Medium'
        }
    },
    {
        id: 'workflow-approvals',
        name: 'Approval Workflows',
        category: 'workflow',
        efficiency: 79,
        default: false,
        description: 'Automated routing of compliance documents and decisions through predefined approval chains. Includes escalation paths and delegation rules.',
        benefits: 'Accelerates approval cycles by 79% and provides complete audit trails. Ensures appropriate oversight for all compliance activities.',
        metrics: {
            timeImpact: 'Very High',
            errorReduction: 'High',
            costSavings: 'High',
            roiContribution: 'Very High'
        }
    },

    {
        id: 'analytics-patterns',
        name: 'Pattern Recognition',
        category: 'analytics',
        efficiency: 75,
        default: false,
        description: 'Advanced analytics to identify trends, patterns, and anomalies in compliance data. Reveals insights that would be missed through manual review.',
        benefits: 'Detects 75% more potential compliance issues before they become problems. Enables proactive risk management.',
        metrics: {
            timeImpact: 'Medium',
            errorReduction: 'Very High',
            costSavings: 'Medium',
            roiContribution: 'High'
        }
    },
    {
        id: 'analytics-anomalies',
        name: 'Anomaly Detection',
        category: 'analytics',
        efficiency: 82,
        default: false,
        description: 'Machine learning algorithms that automatically flag unusual patterns or outliers in compliance data that may indicate risks or errors.',
        benefits: 'Identifies 82% of compliance anomalies before they trigger regulatory issues. Reduces false positives in compliance monitoring.',
        metrics: {
            timeImpact: 'Low',
            errorReduction: 'Very High',
            costSavings: 'High',
            roiContribution: 'Very High'
        }
    },
    {
        id: 'reporting-dashboards',
        name: 'Interactive Dashboards',
        category: 'reporting',
        efficiency: 61,
        default: false,
        description: 'Real-time visual displays of compliance metrics, KPIs, and status indicators. Customizable views for different stakeholder needs.',
        benefits: 'Reduces reporting effort by 61% while providing more actionable insights. Enables data-driven compliance decisions.',
        metrics: {
            timeImpact: 'Medium',
            errorReduction: 'Low',
            costSavings: 'High',
            roiContribution: 'Medium'
        }
    },
    {
        id: 'reporting-automated',
        name: 'Automated Reports',
        category: 'reporting',
        efficiency: 70,
        default: false,
        description: 'Scheduled generation and distribution of compliance reports tailored to specific regulatory frameworks, business units, or leadership needs.',
        benefits: 'Eliminates 70% of manual reporting tasks. Ensures consistent, accurate, and timely delivery of compliance information.',
        metrics: {
            timeImpact: 'High',
            errorReduction: 'Medium',
            costSavings: 'High',
            roiContribution: 'Medium'
        }
    },
    {
        id: 'compliance-tracking',
        name: 'Compliance Tracking',
        category: 'compliance',
        efficiency: 88,
        default: true,
        description: 'Continuous monitoring of compliance status across all regulatory requirements. Real-time tracking of control effectiveness and compliance gaps.',
        benefits: 'Improves compliance visibility by 88% and virtually eliminates compliance surprises. Provides early warning of emerging compliance issues.',
        metrics: {
            timeImpact: 'High',
            errorReduction: 'Very High',
            costSavings: 'Very High',
            roiContribution: 'High'
        }
    },
    {
        id: 'compliance-updates',
        name: 'Regulatory Updates',
        category: 'compliance',
        efficiency: 73,
        default: false,
        description: 'Automated monitoring of regulatory changes that may impact compliance requirements. Includes impact analysis and implementation planning.',
        benefits: 'Reduces regulatory research time by 73% and ensures awareness of new requirements. Prevents compliance gaps due to changing regulations.',
        metrics: {
            timeImpact: 'Medium',
            errorReduction: 'High',
            costSavings: 'High',
            roiContribution: 'Medium'
        }
    },
    {
        id: 'collab-notifications',
        name: 'Smart Notifications',
        category: 'collaboration',
        efficiency: 59,
        default: false,
        description: 'Intelligent alerts and reminders for compliance tasks, deadlines, and issues requiring attention. Prioritized based on risk and urgency.',
        benefits: 'Improves response time to compliance issues by 59%. Ensures critical compliance activities are never overlooked.',
        metrics: {
            timeImpact: 'High',
            errorReduction: 'Medium',
            costSavings: 'Low',
            roiContribution: 'Medium'
        }
    },
    {
        id: 'collab-audit',
        name: 'Audit Trails',
        category: 'collaboration',
        efficiency: 67,
        default: false,
        description: 'Comprehensive documentation of all user actions, decisions, and changes within the compliance system. Creates defensible evidence of compliance activities.',
        benefits: 'Reduces audit preparation time by 67% and strengthens compliance defensibility. Provides complete transparency of compliance processes.',
        metrics: {
            timeImpact: 'Medium',
            errorReduction: 'Medium',
            costSavings: 'Medium',
            roiContribution: 'Very High'
        }
    },

    {
        id: 'security-encryption',
        name: 'Data Encryption',
        category: 'security',
        efficiency: 84,
        default: false,
        description: 'Advanced encryption for sensitive compliance data at rest and in transit. Includes key management and secure access protocols.',
        benefits: 'Reduces data breach risk by 84% and ensures regulatory compliance for sensitive information. Protects the integrity of compliance evidence.',
        metrics: {
            timeImpact: 'Low',
            errorReduction: 'Very High',
            costSavings: 'Medium',
            roiContribution: 'High'
        }
    },
    {
        id: 'ai-summary',
        name: 'AI Document Summary',
        category: 'ai',
        efficiency: 81,
        default: false,
        description: 'AI-powered extraction of key points, requirements, and obligations from lengthy regulatory documents and compliance texts.',
        benefits: 'Reduces document review time by 81% while improving comprehension. Makes complex regulatory information more accessible.',
        metrics: {
            timeImpact: 'Very High',
            errorReduction: 'High',
            costSavings: 'Medium',
            roiContribution: 'High'
        }
    },
    {
        id: 'ai-risk',
        name: 'Risk Assessment',
        category: 'ai',
        efficiency: 77,
        default: false,
        description: 'Machine learning algorithms that analyze compliance data to identify, quantify, and prioritize compliance risks across the organization.',
        benefits: 'Improves risk detection accuracy by 77% and enables proactive mitigation. Provides data-driven risk prioritization.',
        metrics: {
            timeImpact: 'Medium',
            errorReduction: 'High',
            costSavings: 'Medium',
            roiContribution: 'Very High'
        }
    },
    {
        id: 'auto-reminders',
        name: 'Automated Reminders',
        category: 'automation',
        efficiency: 64,
        default: false,
        description: 'Scheduled notifications for recurring compliance tasks, certification renewals, and policy reviews. Ensures nothing falls through the cracks.',
        benefits: 'Eliminates 64% of compliance follow-up activities. Prevents compliance lapses due to missed deadlines.',
        metrics: {
            timeImpact: 'High',
            errorReduction: 'Medium',
            costSavings: 'Medium',
            roiContribution: 'Low'
        }
    },
    {
        id: 'auto-generation',
        name: 'Document Generation',
        category: 'automation',
        efficiency: 71,
        default: false,
        description: 'Automated creation of compliance documents, policies, and reports using templates and real-time data. Ensures consistency and accuracy.',
        benefits: 'Reduces document creation time by 71% while improving quality. Ensures all generated documents meet current regulatory requirements.',
        metrics: {
            timeImpact: 'High',
            errorReduction: 'High',
            costSavings: 'High',
            roiContribution: 'Medium'
        }
    }
];

const METRICS = {
    'time': {
        icon: Clock,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        label: 'Time Saved',
        format: (val) => `${val}%`,
        secondaryFormat: (val) => `${Math.round(val * 0.4)}h/week`,
        baseImpact: 0.75,
        position: {top: '0%', right: '10%'}
    },
    'cost': {
        icon: TrendingDown,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        label: 'Cost Reduction',
        format: (val) => `${val}%`,
        secondaryFormat: (val) => `€${Math.round(val * 250)}k/yr`,
        baseImpact: 0.65,
        position: {top: '25%', right: '10%'}
    },
    'error': {
        icon: AlertCircle,
        color: 'text-purple-600',
        bgColor: 'bg-purple-50',
        label: 'Error Reduction',
        format: (val) => `${val}%`,
        secondaryFormat: (val) => 'Quality',
        baseImpact: 0.85,
        position: {top: '50%', right: '10%'}
    },
    'roi': {
        icon: TrendingUp,
        color: 'text-indigo-600',
        bgColor: 'bg-indigo-50',
        label: 'ROI',
        format: (val) => `${val}%`,
        secondaryFormat: (val) => `${Math.round(val / 100 * 10) / 10}x return`,
        baseImpact: 0.55,
        position: {top: '75%', right: '10%'}
    }
};

const HeroSection = () => {
    const navigate = useNavigate();
    const svgRef = useRef(null);

    // State for active modules and metrics
    const [activeModules, setActiveModules] = useState(
        MODULES.map(module => ({...module, active: module.default}))
    );

    // State for animations and module details
    const [hoveredModuleId, setHoveredModuleId] = useState(null);

    // Calculate active metrics
    const [metrics, setMetrics] = useState({
        time: {value: 0, modules: []},
        cost: {value: 0, modules: []},
        error: {value: 0, modules: []},
        roi: {value: 0, modules: []}
    });

    useEffect(() => {
        const metricContributions = {
            time: {modules: [], value: 0},
            cost: {modules: [], value: 0},
            error: {modules: [], value: 0},
            roi: {modules: [], value: 0}
        };

        activeModules.forEach(module => {
            if (module.active) {
                const categoryInfo = MODULE_CATEGORIES[module.category];

                categoryInfo.metrics.forEach(metric => {
                    metricContributions[metric].modules.push(module.id);
                    const baseImpact = METRICS[metric].baseImpact;
                    const contributionValue = Math.round((module.efficiency / 100) * baseImpact * 25);
                    metricContributions[metric].value += contributionValue;
                });
            }
        });

        Object.keys(metricContributions).forEach(metric => {
            metricContributions[metric].value = Math.min(94, metricContributions[metric].value);
        });

        setMetrics(metricContributions);
    }, [activeModules]);

    // Handle module toggle
    const handleToggleModule = (moduleId) => {
        setActiveModules(currentModules =>
            currentModules.map(module =>
                module.id === moduleId
                    ? {...module, active: !module.active}
                    : module
            )
        );
    };

    // Draw SVG connection lines
    useEffect(() => {
        if (!svgRef.current) return;

        const svg = svgRef.current;
        // Clear existing paths
        while (svg.firstChild) {
            svg.removeChild(svg.firstChild);
        }

        // Get container dimensions
        const container = svg.parentElement;
        const containerRect = container.getBoundingClientRect();

        // Draw paths for each active module
        activeModules.forEach(module => {
            if (!module.active) return;

            const moduleElement = document.getElementById(`module-${module.id}`);
            if (!moduleElement) return;

            const categoryInfo = MODULE_CATEGORIES[module.category];

            categoryInfo.metrics.forEach(metricKey => {
                const metricElement = document.getElementById(`metric-${metricKey}`);
                if (!metricElement) return;

                const moduleRect = moduleElement.getBoundingClientRect();
                const metricRect = metricElement.getBoundingClientRect();

                // Calculate points relative to container
                const startX = moduleRect.right - containerRect.left;
                const startY = moduleRect.top + moduleRect.height / 2 - containerRect.top;

                const endX = metricRect.left - containerRect.left;
                const endY = metricRect.top + metricRect.height / 2 - containerRect.top;

                // Create path with bezier curve
                const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');

                // Control points for the bezier curve
                const controlX1 = startX + (endX - startX) * 0.7;
                const controlY1 = startY;
                const controlX2 = startX + (endX - startX) * 0.3;
                const controlY2 = endY;

                // Path data for a bezier curve
                const pathData = `M ${startX} ${startY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${endX} ${endY}`;

                // Set path attributes
                path.setAttribute('d', pathData);
                path.setAttribute('stroke', '#2563eb'); // Blue-600
                path.setAttribute('stroke-width', '1.5');
                path.setAttribute('fill', 'none');
                path.setAttribute('stroke-dasharray', '4 2');
                path.setAttribute('opacity', '0.7');
                path.setAttribute('data-module-id', module.id);
                path.setAttribute('data-metric', metricKey);

                // Highlight path if module is hovered
                if (hoveredModuleId === module.id) {
                    path.setAttribute('stroke-width', '2.5');
                    path.setAttribute('opacity', '1');
                    path.setAttribute('stroke-dasharray', '1');
                }

                // Add path to SVG
                svg.appendChild(path);
            });
        });
    }, [activeModules, metrics, hoveredModuleId]);

    // Navigation handlers
    const handleCustomizeWorkflowClick = () => {
        navigate('/customize');
    };

    const handleBookDemoClick = () => {
        navigate('/book-demo');
    };

    return (
        <section className="w-full h-full flex flex-col justify-center pt-4 pb-8 overflow-visible ">

            <div
                className="text-center bg-white border-2 border-blue-500 rounded-lg px-8 py-3 text-blue-800 w-full md:w-[40%] relative mx-auto shadow-md">
                <div className="text-xs uppercase tracking-wide font-medium">Total Annual Savings</div>
                <div className="text-2xl font-bold text-blue-600">
                    €{Math.round((metrics.time.value + metrics.cost.value) * 2500).toLocaleString()}
                </div>
            </div>

            <div className="w-full h-full relative overflow-visible">
                <div className="flex flex-col h-[80vh] overflow-visible">


                    <div className="relative flex-grow overflow-visible">


                        {/* SVG for connection lines */}
                        <svg
                            ref={svgRef}
                            className="absolute inset-0 w-full h-full pointer-events-none z-10"
                            xmlns="http://www.w3.org/2000/svg"
                        ></svg>

                        {/* Module Selection Grid */}
                        <div
                            className="absolute  top-[45%] transform -translate-y-1/2 w-[45%] overflow-visible max-h-[70vh] p-2">


                            {/* Module Details Panel - Always visible, updates on hover */}
                            <div
                                className="bg-white hidden rounded-lg border border-gray-200 p-3 shadow-sm h-36 relative overflow-hidden w-full mb-2 ">
                                {!hoveredModuleId ? (
                                    <div
                                        className="flex items-center justify-center h-full text-gray-500 text-sm italic">
                                        Hover over a module to see details
                                    </div>
                                ) : (
                                    (() => {
                                        const hoveredModule = activeModules.find(m => m.id === hoveredModuleId);
                                        if (!hoveredModule) return null;

                                        const categoryInfo = MODULE_CATEGORIES[hoveredModule.category];
                                        const Icon = categoryInfo.icon;

                                        return (
                                            <div className="animate-fadeIn">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className={cn(
                                                        "rounded-full p-1.5",
                                                        categoryInfo.bgColor,
                                                        categoryInfo.color
                                                    )}>
                                                        <Icon className="h-4 w-4"/>
                                                    </div>
                                                    <h4 className="font-semibold">{hoveredModule.name}</h4>
                                                </div>

                                                <p className="text-xs text-gray-700 mb-2">{hoveredModule.description}</p>

                                                <div className="grid grid-cols-4 gap-2">
                                                    <div className="flex items-center gap-1">
                                                        <Clock className="h-3 w-3 text-blue-500"/>
                                                        <span className="text-xs text-gray-700">
                              <span className={cn(
                                  "font-medium",
                                  hoveredModule.metrics.timeImpact === 'Very High' ? "text-blue-700" :
                                      hoveredModule.metrics.timeImpact === 'High' ? "text-blue-600" :
                                          hoveredModule.metrics.timeImpact === 'Medium' ? "text-blue-500" :
                                              "text-gray-500"
                              )}>
                                {hoveredModule.metrics.timeImpact}
                              </span>
                                                        </span>
                                                    </div>

                                                    <div className="flex items-center gap-1">
                                                        <AlertCircle className="h-3 w-3 text-purple-500"/>
                                                        <span className="text-xs text-gray-700">
                              <span className={cn(
                                  "font-medium",
                                  hoveredModule.metrics.errorReduction === 'Very High' ? "text-purple-700" :
                                      hoveredModule.metrics.errorReduction === 'High' ? "text-purple-600" :
                                          hoveredModule.metrics.errorReduction === 'Medium' ? "text-purple-500" :
                                              "text-gray-500"
                              )}>
                                {hoveredModule.metrics.errorReduction}
                              </span>
                            </span>
                                                    </div>

                                                    <div className="flex items-center gap-1">
                                                        <TrendingDown className="h-3 w-3 text-blue-500"/>
                                                        <span className="text-xs text-gray-700">
                              <span className={cn(
                                  "font-medium",
                                  hoveredModule.metrics.costSavings === 'Very High' ? "text-blue-700" :
                                      hoveredModule.metrics.costSavings === 'High' ? "text-blue-600" :
                                          hoveredModule.metrics.costSavings === 'Medium' ? "text-blue-500" :
                                              "text-gray-500"
                              )}>
                                {hoveredModule.metrics.costSavings}
                              </span>
                            </span>
                                                    </div>

                                                    <div className="flex items-center gap-1">
                                                        <TrendingUp className="h-3 w-3 text-indigo-500"/>
                                                        <span className="text-xs text-gray-700">
                              <span className={cn(
                                  "font-medium",
                                  hoveredModule.metrics.roiContribution === 'Very High' ? "text-indigo-700" :
                                      hoveredModule.metrics.roiContribution === 'High' ? "text-indigo-600" :
                                          hoveredModule.metrics.roiContribution === 'Medium' ? "text-indigo-500" :
                                              "text-gray-500"
                              )}>
                                {hoveredModule.metrics.roiContribution}
                              </span>
                            </span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()
                                )}
                            </div>


                            <div className="grid grid-cols-2 gap-2">
                                {activeModules.map((module) => {
                                    const categoryInfo = MODULE_CATEGORIES[module.category];
                                    const Icon = categoryInfo.icon;

                                    return (
                                        <div
                                            key={module.id}
                                            className={cn(
                                                "relative p-3 rounded-lg border cursor-pointer transition-all duration-200 group",
                                                module.active
                                                    ? `${categoryInfo.bgColor} border-2 ${categoryInfo.borderColor} shadow-sm`
                                                    : "bg-white border-gray-200 hover:bg-gray-50 border-2",
                                                hoveredModuleId === module.id ? "scale-105 shadow-md z-20" : "z-10"
                                            )}
                                            onClick={() => handleToggleModule(module.id)}
                                            onMouseEnter={() => setHoveredModuleId(module.id)}
                                            onMouseLeave={() => setHoveredModuleId(null)}
                                            id={`module-${module.id}`}
                                            data-active={module.active}
                                            data-category={module.category}
                                        >
                                            <div className="flex items-center gap-2">
                                                <div className={cn(
                                                    "rounded-full p-1.5",
                                                    categoryInfo.bgColor,
                                                    categoryInfo.color
                                                )}>
                                                    <Icon className="h-4 w-4"/>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className={cn(
                                                        "text-sm font-medium truncate",
                                                        module.active ? "text-gray-800" : "text-gray-600"
                                                    )}>
                                                        {module.name}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        {module.efficiency}% efficiency
                                                    </p>
                                                </div>
                                                <div className={cn(
                                                    "h-5 w-5 rounded-full flex items-center justify-center",
                                                    module.active
                                                        ? "bg-white"
                                                        : "bg-gray-100"
                                                )}>
                                                    {module.active && (
                                                        <CheckCircle2 className="h-4 w-4 text-blue-500"/>
                                                    )}
                                                </div>
                                            </div>


                                            {/* Active indicator */}
                                            {module.active && (
                                                <div
                                                    className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-blue-500 animate-pulse"/>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Metrics Display */}
                        <div className="absolute right-0 top-0 bottom-0 w-[40%]  z-1000">
                            {Object.entries(METRICS).map(([key, metric]) => {
                                const value = metrics[key].value;
                                const hasActiveModules = metrics[key].modules.length > 0;
                                const isHighlighted = hoveredModuleId && activeModules.find(
                                    m => m.id === hoveredModuleId && m.active && MODULE_CATEGORIES[m.category].metrics.includes(key)
                                );

                                return (
                                    <div
                                        key={key}
                                        className="absolute transform transition-all duration-300 "
                                        style={metric.position}
                                    >
                                        <Card
                                            className={cn(
                                                "w-64 transition-all duration-300 overflow-hidden relative",
                                                hasActiveModules ? "shadow-md" : "shadow border-gray-200",
                                                isHighlighted ? "ring-2 border-l-blue-300 scale-105 shadow-lg" : ""
                                            )}
                                            id={`metric-${key}`}
                                        >
                                            <CardContent className="p-4">
                                                <div className="flex justify-between items-center mb-2">
                                                    <div className="flex items-center gap-2">
                                                        <div
                                                            className={cn("rounded-full p-1.5", metric.bgColor, metric.color)}>
                                                            <metric.icon className="h-4 w-4"/>
                                                        </div>
                                                        <h4 className="font-medium text-gray-700">{metric.label}</h4>
                                                    </div>
                                                    <div
                                                        className={`text-lg font-semibold ${metric.color} transition-all duration-700`}>
                                                        {metric.format(value)}
                                                    </div>
                                                </div>

                                                <div className="flex justify-between items-center">
                                                    <div className="text-xs text-gray-500">
                                                        {metrics[key].modules.length} module{metrics[key].modules.length !== 1 ? 's' : ''} contributing
                                                    </div>
                                                    <div
                                                        className="text-xs bg-gray-100 rounded-full px-2 py-0.5 text-gray-600">
                                                        {metric.secondaryFormat(value)}
                                                    </div>
                                                </div>

                                                <div className="mt-2 bg-gray-100 h-2 rounded-full overflow-hidden">
                                                    <div
                                                        className={cn("h-full rounded-full transition-all duration-700", metric.bgColor)}
                                                        style={{width: `${value}%`}}
                                                    />
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </div>
                                );
                            })}
                        </div>


                    </div>


                </div>


            </div>


        </section>
    );
};

export default HeroSection;
